import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import {
  Coins,
  ShareNetwork,
  ArrowSquareOut,
  Check,
} from "@phosphor-icons/react";
import ArtifactPublicDetail from "../components/ArtifactPublicDetail";

// Best-effort update of <title> and OpenGraph meta so link previews on
// WhatsApp / Telegram / social show the actual artifact rather than
// the app's generic homepage.
function useShareMeta(item) {
  useEffect(() => {
    if (!item) return;
    const ident = item.identification || {};
    const title = ident.nome
      ? `${ident.nome} · Reperti`
      : "Scheda pubblica · Reperti";
    const desc = ident.descrizione
      ? String(ident.descrizione).slice(0, 200)
      : "Reperto identificato con AI dal catalogo Reperti.";
    const image = item.photos?.[0] ? fileUrl(item.photos[0]) : "";

    const prevTitle = document.title;
    document.title = title;

    const setMeta = (attr, key, value) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:type", "article");
    if (image) setMeta("property", "og:image", image);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "description", desc);

    return () => {
      document.title = prevTitle;
    };
  }, [item]);
}

export default function PublicShare() {
  const { token } = useParams();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  const doShare = async () => {
    const ident = item?.identification || {};
    const title = ident.nome ? `${ident.nome} · Reperti` : "Scheda Reperti";
    const text = ident.descrizione
      ? String(ident.descrizione).slice(0, 140)
      : "Guarda questo reperto identificato con AI.";
    if (navigator.share) {
      try {
        await navigator.share({ title, text, url: shareUrl });
        return;
      } catch (e) {
        // user cancelled — fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // clipboard blocked (rare, e.g. insecure context) — do nothing
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/artifacts/${token}`);
        setItem(data);
      } catch (e) {
        setErr("Questo reperto non è più disponibile pubblicamente.");
      }
    })();
  }, [token]);

  useShareMeta(item);

  if (err) {
    return (
      <div
        className="max-w-xl mx-auto px-5 py-20 text-center fade-in"
        data-testid="public-share-error"
      >
        <h1 className="font-serif text-3xl text-[#1E2520] mb-3">
          Reperto non disponibile
        </h1>
        <p className="text-[#5C635A]">{err}</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#9C5B42] hover:text-[#7A4632]"
        >
          Torna a Reperti <ArrowSquareOut size={14} />
        </Link>
      </div>
    );
  }
  if (!item) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20">
        <div className="h-10 w-2/3 scanning" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#F9F8F6]"
      data-testid="public-share-page"
    >
      <header className="border-b border-black/5 bg-[#F9F8F6]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center bg-[#9C5B42] text-white">
              <Coins weight="duotone" size={20} />
            </span>
            <div className="leading-tight">
              <div className="font-serif text-2xl text-[#1E2520]">Reperti</div>
              <div className="label-eyebrow">Scheda pubblica</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={doShare}
              className="inline-flex items-center gap-1.5 text-sm bg-[#9C5B42] hover:bg-[#7A4632] text-white px-3.5 py-2 rounded-sm transition-colors"
              data-testid="public-share-btn-header"
              aria-label="Condividi questo reperto"
            >
              {copied ? (
                <><Check weight="bold" size={14} /> Copiato</>
              ) : (
                <><ShareNetwork weight="duotone" size={14} /> Condividi</>
              )}
            </button>
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#9C5B42] hover:text-[#7A4632]"
              data-testid="public-cta-open-app"
            >
              Esplora l&apos;app <ArrowSquareOut size={14} />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14 fade-in">
        <ArtifactPublicDetail item={item} />

        <div className="border-t border-black/5 pt-8 text-center">
          <div className="inline-flex items-center gap-2 label-eyebrow mb-4">
            <ShareNetwork weight="duotone" /> Condiviso da Reperti
          </div>
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={doShare}
              className="inline-flex items-center gap-2 bg-[#9C5B42] hover:bg-[#7A4632] text-white px-5 py-2.5 rounded-sm text-sm transition-colors"
              data-testid="public-share-btn-footer"
            >
              {copied ? (
                <><Check weight="bold" size={16} /> Link copiato</>
              ) : (
                <><ShareNetwork weight="duotone" size={16} /> Condividi questo reperto</>
              )}
            </button>
            <p className="text-sm text-[#5C635A] max-w-md">
              Catalogo personale di reperti trovati col metaldetector, identificati
              dall&apos;AI.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
